/* La Caza Cali — Cart Drawer + AJAX Cart Engine (Phase 2.3)

   Pure vanilla, no dependencies. Loaded via <script defer> (will be added to
   theme.liquid in diff 7). Runs after DOM parse.

   ───────────────────────────────────────────────────────────────────────
   Responsibilities (commented per section below):

   1. DRAWER OPEN / CLOSE
        — [data-cart-drawer-toggle]   opens drawer
        — [data-cart-drawer-close]    closes drawer (close button + scrim)
        — Escape key                  closes drawer + restores focus to toggle
        — body.style.overflow lock    prevents background scroll while open
        — aria-hidden state mgmt      a11y for screen readers

   2. ADD-TO-CART FORM INTERCEPT
        — Captures submit on form[action*="/cart/add"]
        — Uses CAPTURE phase to fire BEFORE Nordic's product-form.js handler
        — stopImmediatePropagation prevents Nordic from also handling
        — Posts to /cart/add.js with sections=lacaza-cart-drawer to get
          rendered drawer HTML in same response (single round trip)
        — Refreshes drawer DOM, updates count badges, opens drawer

   3. QUANTITY STEPPER (+ / −)
        — [data-cart-qty="inc"|"dec"] click handler
        — Reads current qty from [data-line-qty]
        — POSTs to /cart/change.js with new quantity + sections param
        — Drawer + count badges update

   4. REMOVE LINE ITEM
        — [data-cart-remove] click handler
        — POSTs to /cart/change.js with quantity=0 (Shopify removes)
        — Drawer + count badges update

   5. CART COUNT BADGE LIVE UPDATE
        — All elements with [data-cart-count] receive new count text
        — When count > 0: remove .is-empty class
        — When count = 0: add .is-empty class (CSS hides via diff 8)

   6. APP COMPATIBILITY EVENT
        — document.dispatchEvent('cart:updated', { detail: cart })
        — Fired after every successful cart mutation
        — Klaviyo / Discounty / abandoned-cart trackers can listen

   7. ERROR HANDLING
        — Every fetch wrapped in try/catch
        — console.error('[lacaza-cart] ...') on failure
        — Drawer state preserved on error — never breaks visually
*/

(function () {
  'use strict';

  /* Section ID for the ?sections= API endpoint. Must match the section file
     name (sections/lacaza-cart-drawer.liquid → 'lacaza-cart-drawer'). */
  var SECTION_ID = 'lacaza-cart-drawer';

  var body = document.body;

  /* Lazy lookups — drawer DOM may be replaced by refresh, so don't cache */
  function getDrawer()    { return document.querySelector('[data-cart-drawer]'); }
  function getToggleBtn() { return document.querySelector('[data-cart-drawer-toggle]'); }


  /* ─────────────────────────────────────────────────────────────────────
     1. DRAWER OPEN / CLOSE
     ───────────────────────────────────────────────────────────────────── */

  function openDrawer() {
    body.classList.add('lacaza-cart-drawer-open');
    body.style.overflow = 'hidden';

    var drawer = getDrawer();
    if (drawer) {
      drawer.setAttribute('aria-hidden', 'false');
      /* Focus the close button after the slide-in animation begins.
         requestAnimationFrame ensures the element is paint-rendered first. */
      var closeBtn = drawer.querySelector('.lacaza-cart-drawer__close');
      if (closeBtn) requestAnimationFrame(function () { closeBtn.focus(); });
    }
  }

  function closeDrawer() {
    body.classList.remove('lacaza-cart-drawer-open');
    body.style.overflow = '';

    var drawer = getDrawer();
    if (drawer) drawer.setAttribute('aria-hidden', 'true');
  }


  /* ─────────────────────────────────────────────────────────────────────
     CLICK DELEGATE — handles toggle, close, qty stepper, remove
     ───────────────────────────────────────────────────────────────────── */

  document.addEventListener('click', function (e) {

    /* Open drawer when any [data-cart-drawer-toggle] is clicked.
       Header cart icon (post diff 6) will have this attribute. */
    if (e.target.closest('[data-cart-drawer-toggle]')) {
      e.preventDefault();
      openDrawer();
      return;
    }

    /* Close drawer when scrim, X button, or any [data-cart-drawer-close]
       element is clicked. */
    if (e.target.closest('[data-cart-drawer-close]')) {
      e.preventDefault();
      closeDrawer();
      return;
    }

    /* Quantity stepper — −/+ buttons inside cart items */
    var qtyBtn = e.target.closest('[data-cart-qty]');
    if (qtyBtn) {
      e.preventDefault();
      handleQtyChange(qtyBtn);
      return;
    }

    /* Remove button */
    var removeBtn = e.target.closest('[data-cart-remove]');
    if (removeBtn) {
      e.preventDefault();
      handleRemove(removeBtn);
      return;
    }
  });


  /* ─────────────────────────────────────────────────────────────────────
     ESCAPE KEY — closes drawer + returns focus to toggle button
     ───────────────────────────────────────────────────────────────────── */

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && body.classList.contains('lacaza-cart-drawer-open')) {
      closeDrawer();
      var toggle = getToggleBtn();
      if (toggle) toggle.focus();
    }
  });


  /* ─────────────────────────────────────────────────────────────────────
     2. ADD-TO-CART FORM INTERCEPT (capture phase)

     The capture: true argument is critical. Nordic's product-form.js
     attaches its handler directly to the form, which fires at TARGET
     phase. Document-level capture-phase listeners run BEFORE target
     phase, letting us preventDefault() and stopImmediatePropagation()
     before Nordic's handler can do anything.

     Selector: form[action*="/cart/add"]  matches Shopify add-to-cart
     forms (action="/cart/add"). Stoq's "notify when back in stock"
     forms post to a different endpoint (typically /contact or Stoq's
     own URL). Klaviyo signup forms use /contact. Neither match.
     ───────────────────────────────────────────────────────────────────── */

  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form[action*="/cart/add"]');
    if (!form) return;

    /* Stop the page navigation AND prevent Nordic's handler */
    e.preventDefault();
    e.stopImmediatePropagation();

    handleAddToCart(form);
  }, true /* capture */);

  async function handleAddToCart(form) {
    try {
      var fd = new FormData(form);
      /* Tell Shopify to also return the rendered drawer section in the
         response. Saves an extra round trip. */
      fd.append('sections', SECTION_ID);

      var addRes = await fetch('/cart/add.js', {
        method: 'POST',
        body: fd,
        headers: { 'Accept': 'application/json' }
      });

      if (!addRes.ok) {
        /* Shopify returns descriptive error JSON on validation failures
           (variant unavailable, sold out, etc.). Surface to console for now;
           future enhancement can show a toast. */
        var errBody = await addRes.json().catch(function () { return {}; });
        throw new Error(errBody.description || errBody.message || 'Add to cart failed');
      }

      var addData = await addRes.json();

      /* Replace drawer markup with the freshly rendered section. */
      if (addData.sections && addData.sections[SECTION_ID]) {
        replaceDrawerContent(addData.sections[SECTION_ID]);
      }

      /* /cart/add.js response is the LINE ITEM, not the full cart. To get
         total item_count for the count badge + cart:updated event detail,
         do one more fetch. /cart.js is fast — Shopify caches it. */
      var cart = await fetchCart();
      updateCartCountBadges(cart.item_count);

      /* Slide drawer in to show the user their newly added item */
      openDrawer();

      /* App compatibility event — Klaviyo, Discounty, etc. listen for this */
      dispatchCartUpdated(cart);

    } catch (err) {
      console.error('[lacaza-cart] Add to cart failed:', err);
    }
  }


  /* ─────────────────────────────────────────────────────────────────────
     3 + 4. QUANTITY CHANGE / REMOVE — both go through /cart/change.js
     ───────────────────────────────────────────────────────────────────── */

  function handleQtyChange(btn) {
    var lineEl = btn.closest('[data-line-item]');
    if (!lineEl) return;

    var lineKey = lineEl.dataset.lineItem;
    var qtyEl = lineEl.querySelector('[data-line-qty]');
    if (!qtyEl) return;

    var currentQty = parseInt(qtyEl.textContent.trim(), 10) || 0;
    var action = btn.dataset.cartQty;
    var newQty;

    if (action === 'inc')      newQty = currentQty + 1;
    else if (action === 'dec') newQty = Math.max(0, currentQty - 1);
    else return;

    mutateCart({ id: lineKey, quantity: newQty });
  }

  function handleRemove(btn) {
    var lineEl = btn.closest('[data-line-item]');
    if (!lineEl) return;
    var lineKey = lineEl.dataset.lineItem;
    /* quantity: 0 tells Shopify to remove the line entirely */
    mutateCart({ id: lineKey, quantity: 0 });
  }

  async function mutateCart(payload) {
    try {
      /* /cart/change.js response IS the full cart (not just the changed line).
         Pass sections param to get drawer HTML in the same response. */
      var res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: payload.id,
          quantity: payload.quantity,
          sections: SECTION_ID
        })
      });

      if (!res.ok) {
        var errBody = await res.json().catch(function () { return {}; });
        throw new Error(errBody.description || errBody.message || 'Cart change failed');
      }

      var data = await res.json();

      if (data.sections && data.sections[SECTION_ID]) {
        replaceDrawerContent(data.sections[SECTION_ID]);
      }

      updateCartCountBadges(data.item_count);
      dispatchCartUpdated(data);

    } catch (err) {
      console.error('[lacaza-cart] Cart change failed:', err);
    }
  }


  /* ─────────────────────────────────────────────────────────────────────
     HELPERS
     ───────────────────────────────────────────────────────────────────── */

  /* Swap the live drawer's innerHTML with the freshly-rendered HTML.
     Preserves the live <aside> wrapper (its data attrs + open/close state). */
  function replaceDrawerContent(html) {
    var tmp = document.createElement('div');
    tmp.innerHTML = html;
    var newAside = tmp.querySelector('[data-cart-drawer]');
    var liveAside = getDrawer();
    if (newAside && liveAside) {
      liveAside.innerHTML = newAside.innerHTML;
    }
  }

  /* Update text on every [data-cart-count] element (header badge + drawer count).
     Toggle .is-empty class so CSS can hide the header badge when count is 0. */
  function updateCartCountBadges(count) {
    var badges = document.querySelectorAll('[data-cart-count]');
    for (var i = 0; i < badges.length; i++) {
      badges[i].textContent = count;
      if (count > 0) {
        badges[i].classList.remove('is-empty');
      } else {
        badges[i].classList.add('is-empty');
      }
    }
  }

  /* Fetch authoritative cart state — used after /cart/add.js since that
     endpoint returns the line item, not the full cart. */
  async function fetchCart() {
    var res = await fetch('/cart.js', {
      headers: { 'Accept': 'application/json' }
    });
    return res.json();
  }

  /* Dispatch app-compatibility event with full cart payload */
  function dispatchCartUpdated(cart) {
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
  }

})();
